/**
 * CategoryChecklist — multi-select list of dispatch day categories.
 *
 * Shared by the double-click quick menu (TimeOffMenu) and the multi-day
 * editor (TimeOffModal) so both commit several categories at once.
 * Categories flagged `capture_details` reveal inline hours/notes inputs
 * when ticked. Rows are grouped by kind: Off (red) then Unavailable (orange).
 */
import { Check } from "lucide-react";
import { CATEGORY_KINDS, normalizeKind } from "@/lib/dispatchCategories";

export const catSlug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-");

const Row = ({ c, on, details, onToggle, onDetailChange, testPrefix }) => {
  const slug = catSlug(c.label);
  const color = CATEGORY_KINDS[normalizeKind(c)].color;
  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <button
        type="button"
        onClick={() => onToggle(c.label)}
        className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50 ${on ? "bg-slate-50 font-medium" : ""}`}
        data-testid={`${testPrefix}-${slug}`}
      >
        <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${on ? "bg-slate-900 border-slate-900" : "border-slate-300 bg-white"}`}>
          {on && <Check className="w-3 h-3 text-white" />}
        </span>
        <span className="cat-swatch w-3 h-3 rounded-sm border border-slate-300 shrink-0" style={{ "--cat": color }} />
        <span className="text-left flex-1 truncate">{c.label}</span>
        {c.blocks_dispatch === false && <span className="text-[9px] text-slate-400 shrink-0">open</span>}
      </button>
      {on && c.capture_details && (
        <div className="px-3 pb-2 space-y-1.5" data-testid={`${testPrefix}-details-${slug}`}>
          <input
            type="number"
            min="0"
            step="0.5"
            value={details[c.label]?.hours ?? 10}
            onChange={e => onDetailChange?.(c.label, { hours: e.target.value })}
            className="w-full h-8 px-2 text-xs border border-slate-200 rounded"
            placeholder="Hours per day"
            data-testid={`${testPrefix}-hours-${slug}`}
          />
          <textarea
            value={details[c.label]?.notes ?? ""}
            onChange={e => onDetailChange?.(c.label, { notes: e.target.value })}
            className="w-full text-xs border border-slate-200 rounded px-2 py-1 min-h-[44px]"
            placeholder="Project, foreman, jobsite…"
            data-testid={`${testPrefix}-notes-${slug}`}
          />
        </div>
      )}
    </div>
  );
};

export const CategoryChecklist = ({ categories, selected, details = {}, onToggle, onDetailChange, maxHeight = 260, testPrefix = "cat" }) => (
  <div className="overflow-y-auto" style={{ maxHeight }}>
    {Object.entries(CATEGORY_KINDS).map(([kind, meta]) => {
      const rows = categories.filter(c => normalizeKind(c) === kind && !(c.deprecated && !selected.includes(c.label)));
      if (!rows.length) return null;
      return (
        <div key={kind} data-testid={`${testPrefix}-group-${kind}`}>
          <div
            className="flex items-center gap-1.5 px-3 pt-1.5 pb-1 text-[10px] font-semibold uppercase tracking-wide"
            style={{ color: meta.color }}
            title={meta.hint}
          >
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: meta.color }} />
            {meta.label}
          </div>
          {rows.map(c => (
            <Row key={c.id} c={c} on={selected.includes(c.label)} details={details}
                 onToggle={onToggle} onDetailChange={onDetailChange} testPrefix={testPrefix} />
          ))}
        </div>
      );
    })}
  </div>
);
